/* eslint-disable no-alert */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, CardTitle, Button,
  Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';

import moment from 'moment';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { del, post } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import PutawayAreaForm from '../../components/Warehouse/PutawayAreaForm';

function LotMapping(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  const [indexing, setIndexing] = useState(false);
  const [signal, setSignal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [entity, setEntity] = useState(null);
  const [areaToDelete, setAreaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { t } = props;
  const isDarkMode = props.layoutModeType === 'dark';

  const toggleModal = () => setShowModal(!showModal);

  const createArea = () => {
    setEntity(null);
    toggleModal();
  };

  const closeDeleteModal = () => {
    if (!deleting) setAreaToDelete(null);
  };

  const deleteArea = async () => {
    if (!areaToDelete || deleting) return;
    setDeleting(true);
    try {
      await del(`/api/warehouse/putawayarea/${areaToDelete.id}`);

      toastr.success(t('Area deleted successfully'));
      setAreaToDelete(null);
      setSignal((previous) => previous + 1);
    } catch (error) {
      toastr.error(error.message, t('Could not delete area.'));
    } finally {
      setDeleting(false);
    }
  };

  const indexAreas = async () => {
    if (indexing) return;
    setIndexing(true);
    try {
      await post('/api/warehouse/putawayarea/index');

      toastr.success(t('Areas indexed successfully'), '', {
        positionClass: 'toast-top-right', timeOut: 8000, preventDuplicates: false,
      });
      setSignal((previous) => previous + 1);
    } catch (error) {
      toastr.error(error.response?.data?.error || error.message, t('Could not index areas.'), {
        positionClass: 'toast-top-right', timeOut: 8000, preventDuplicates: false,
      });
    } finally {
      setIndexing(false);
    }
  };

  const handleSuccess = () => {
    toastr.success(t('Area saved and indexed successfully'));
    setShowModal(false);
    setSignal(signal + 1);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">
                  <span className="mdi mdi-border-top fs-5" />
                  {' '}
                  {t('Put-Away Lot Mapping')}
                </CardTitle>

                <Can i="see" this="putaway">
                  <Button size="sm" color="primary" className="mb-2" onClick={indexAreas} disabled={indexing}>
                    {t('Index Areas')}
                  </Button>
                  <BaseGrid
                    url="/api/warehouse/putawayarea"
                    shouldUpdate={signal}
                    hasSearch
                    create={createArea}
                    config={
                      {
                        columns: {
                          id: {
                            label: 'ID',
                            sort: true,
                          },
                          name: {
                            label: t('Name'),
                          },
                          priority: {
                            label: t('Priority'),
                            html: true,
                            format: (value) => {
                              // Dark mode lifts the red/blue scale so low values stay visible.
                              let r = 0;
                              let g = 0;
                              let b = 0;

                              if (isDarkMode) {
                                if (value > 128) {
                                  const intensity = (value - 128) / 127;
                                  r = 255;
                                  g = Math.round(182 - (intensity * 132));
                                  b = Math.round(196 - (intensity * 156));
                                } else {
                                  const intensity = value / 128;
                                  r = Math.round(110 - (intensity * 48));
                                  g = Math.round(148 + (intensity * 34));
                                  b = Math.round(188 + (intensity * 54));
                                }
                              } else if (value > 128) {
                                r = value;
                              } else {
                                b = value;
                              }

                              return `<span style="color: rgb(${r}, ${g}, ${b})">${value}</span>`;
                            },
                          },
                          location_query: {
                            label: t('Locations'),
                            style: { minWidth: '100px' },
                            html: true,
                            format: (value) => (value.split('\n').join('<br />')),
                          },
                          lot_query: {
                            label: t('Lots'),
                            style: { maxWidth: '200px' },
                          },
                          stock_id_query: {
                            label: t('Stock IDs'),
                            style: { maxWidth: '200px' },
                            format: (value) => (value.length > 85 ? `${value.slice(0, 85).trim()}…` : value),
                          },
                          created_at: {
                            label: 'Created',
                            date: {
                              format: (date) => (moment(date).fromNow()),
                              timezone: process.env.REACT_APP_LOCAL_TZ,
                            },
                            sort: true,
                          },
                          actions: {
                            label: 'Actions',
                            actions: [
                              {
                                key: 'edit_area',
                                icon: 'mdi mdi-pencil-outline',
                                class: 'btn btn-sm btn-soft-warning text-dark',
                                alt: 'Edit Area',
                                click: (area) => {
                                  setEntity(area);
                                  toggleModal();
                                },
                              },
                              {
                                key: 'remove_area',
                                icon: 'mdi mdi-close-thick',
                                class: 'btn btn-sm btn-soft-danger text-dark',
                                alt: 'Delete Area',
                                click: (area) => setAreaToDelete(area),
                              },
                            ],
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 30,
                        },
                      }
                    }
                  />

                  <Modal isOpen={areaToDelete !== null} toggle={closeDeleteModal}>
                    <ModalHeader toggle={deleting ? undefined : closeDeleteModal}>
                      {t('Delete Put-Away Area')}
                    </ModalHeader>
                    <ModalBody>
                      {t('Are you sure you want to delete this putaway area?')}
                      <div className="fw-bold mt-2">{areaToDelete?.name}</div>
                      <p className="text-muted mt-2 mb-0">{t('This action cannot be undone.')}</p>
                    </ModalBody>
                    <ModalFooter>
                      <Button size="sm" color="light" onClick={closeDeleteModal} disabled={deleting}>
                        {t('Cancel')}
                      </Button>
                      <Button size="sm" color="danger" onClick={deleteArea} disabled={deleting}>
                        {deleting ? t('Deleting…') : t('Delete Area')}
                      </Button>
                    </ModalFooter>
                  </Modal>

                  <Modal isOpen={showModal} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>{t('Define Put-Away Area')}</ModalHeader>
                    <ModalBody>
                      <PutawayAreaForm onSuccess={handleSuccess} entity={entity} />
                    </ModalBody>
                    <ModalFooter>
                      <Button size="sm" color="light" onClick={toggleModal}>
                        {t('Cancel')}
                      </Button>
                      <Button size="sm" color="primary" form="PutawayAreaForm" type="submit">
                        {t('Save')}
                      </Button>
                    </ModalFooter>
                  </Modal>
                </Can>

                <Can not i="see" this="putaway"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

LotMapping.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  layoutModeType: PropTypes.string,
};

LotMapping.defaultProps = {
  layoutModeType: 'light',
};

export default connect((state) => ({
  layoutModeType: state.Layout.layoutModeType,
}), {})(withTranslation()(LotMapping));
